import type { Crepe } from '@milkdown/crepe';
import { parserCtx, schemaCtx } from '@milkdown/kit/core';
import type { EditorView } from '@milkdown/kit/prose/view';
import type { Schema } from '@milkdown/kit/prose/model';
import { Plugin } from '@milkdown/kit/prose/state';
import { $prose } from '@milkdown/kit/utils';

import type { MediaUploadHandler } from '../types';
import { isAcceptedAttachmentFile, uploadAndInsertAttachments } from '../nodes/attachment/attachmentUpload';
import { isVideoFile } from '../nodes/video';
import { dataUriToFile, extractDataUriImagesFromClipboard, getClipboardFiles } from './dataUriImageClipboard';
import { defaultMediaUpload, runEditorMediaUpload } from './runEditorMediaUpload';
import type { ClipboardMarkdownParser } from './tableCellImage';
import { isInTableCellPos } from './tableCellHardBreak';
import { uploadPlaceholderDecorationPlugin } from './uploadPlaceholderDecoration';
import { createMediaNode } from './uploadPlugin/createMediaNode';
import { insertOrderedDataUriClipboardContent } from './uploadPlugin/insertOrderedDataUriClipboardContent';

/** Re-export for tests and callers that imported `defaultUpload` from this module */
export const defaultUpload = defaultMediaUpload;

export const isImageFileMIME = (file: File) => file.type.startsWith('image/');
export const isVideoFileMIME = (file: File) => file.type.startsWith('video/') || isVideoFile(file.name);
export const isMediaFile = (file: File) => isImageFileMIME(file) || isVideoFileMIME(file);

export interface UploadPluginOptions {
  onUploadImage?: MediaUploadHandler;
  onUploadVideo?: MediaUploadHandler;
  onUploadFile?: MediaUploadHandler;
}

export const isUploadableEditorFile = (file: File) => isMediaFile(file) || isAcceptedAttachmentFile(file);

/**
 * Creates event handlers for file upload. Exported for testing.
 *
 * `getParser` must be called at paste time (not factory time): `$prose` only waits for
 * `SchemaReady`, so `parserCtx` may still be the out-of-scope stub when the plugin is built.
 */
export function createUploadHandlers(
  options: UploadPluginOptions,
  schema: Schema,
  abortSignal: AbortSignal,
  getParser?: () => ClipboardMarkdownParser,
) {
  const uploadImage = options.onUploadImage ?? defaultMediaUpload;
  const uploadVideo = options.onUploadVideo ?? defaultMediaUpload;
  const uploadFile = options.onUploadFile ?? defaultMediaUpload;

  const queueMediaUploads = (files: File[], view: EditorView) => {
    const media = files.filter(isMediaFile);
    if (!media.length || abortSignal.aborted) return;

    const widgetPos = view.state.selection.from;
    const asInlineImage = isInTableCellPos(view.state.selection.$from);

    for (const file of media) {
      const kind = isImageFileMIME(file) ? 'image' : 'video';
      runEditorMediaUpload({
        view,
        file,
        kind,
        widgetPos,
        upload: kind === 'image' ? uploadImage : uploadVideo,
        abortSignal,
        createNode: (src, f) => createMediaNode(schema, kind, src, f, asInlineImage),
        logLabel: '[uploadPlugin]',
      });
    }
  };

  const queueAttachmentUploads = (files: File[], view: EditorView) => {
    if (abortSignal.aborted) return;

    void uploadAndInsertAttachments(view, files, uploadFile, { abortSignal });
  };

  const queueUploads = (files: File[], view: EditorView) => {
    queueMediaUploads(files, view);
    queueAttachmentUploads(files, view);
  };

  return {
    drop: (view: EditorView, event: Event) => {
      const files = (event as DragEvent).dataTransfer?.files;
      if (!files?.length || view.props.editable?.(view.state) === false || !Array.from(files).some(isUploadableEditorFile)) {
        return false;
      }
      event.preventDefault();
      queueUploads(Array.from(files), view);

      return true;
    },
    dragover: (_view: EditorView, event: Event) => {
      if (!(event as DragEvent).dataTransfer?.types.includes('Files')) return false;
      event.preventDefault();

      return true;
    },
    paste: (view: EditorView, event: Event) => {
      const { clipboardData } = event as ClipboardEvent;
      if (view.props.editable?.(view.state) === false || !clipboardData) return false;
      if (view.state.selection.$from.node().type.spec.code) return false;

      const attachmentFiles = getClipboardFiles(clipboardData, isAcceptedAttachmentFile);
      const mediaFiles = getClipboardFiles(clipboardData, isMediaFile);
      if (attachmentFiles.length || mediaFiles.length) {
        event.preventDefault();
        queueUploads([...mediaFiles, ...attachmentFiles], view);

        return true;
      }

      const dataUriImages = extractDataUriImagesFromClipboard(clipboardData);
      if (!dataUriImages.length) return false;

      event.preventDefault();

      if (getParser) {
        void insertOrderedDataUriClipboardContent(view, schema, getParser(), clipboardData, uploadImage, abortSignal).catch(
          (error) => {
            console.error('[uploadPlugin] Failed to insert data-URI clipboard content:', error);
          },
        );
      } else {
        void (async () => {
          const files: File[] = [];
          for (let i = 0; i < dataUriImages.length; i++) {
            try {
              files.push(await dataUriToFile(dataUriImages[i]!, `pasted-image-${i + 1}`));
            } catch (error) {
              console.error('[uploadPlugin] Failed to decode data-URI image:', error);
            }
          }
          queueMediaUploads(files, view);
        })();
      }

      return true;
    },
  };
}

function createFileUploadPlugin(options: UploadPluginOptions) {
  return $prose((ctx) => {
    const abortController = new AbortController();
    const handlers = createUploadHandlers(options, ctx.get(schemaCtx), abortController.signal, () => ctx.get(parserCtx));

    return new Plugin({
      props: { handleDOMEvents: handlers },
      destroy() {
        abortController.abort();
      },
    });
  });
}

export function configureUpload(crepe: Crepe, options: UploadPluginOptions = {}) {
  crepe.editor.use(uploadPlaceholderDecorationPlugin);
  crepe.editor.use(createFileUploadPlugin(options));
}
